/**
 * MUI Storage component that replaces the styled-components Storage component
 * 
 * This component provides a Material UI equivalent of the original Storage component
 * with similar props and functionality.
 */
import React, { Fragment } from "react";
import { styled } from "@mui/material/styles";
import { Typography } from "@mui/material";
import { muiStorageElements } from "./MuiStorageElements";
import { useSelector } from "react-redux";
import { getConditionResult } from "reduxModules/selectors/containerSelectors";
import { getDrawingBox } from "reduxModules/ducks/ui";
import constants from "toolConstants/warehouseDesigner/constants";
import { 
  MuiStorageProps, 
  UsageLabelProps 
} from "types/components/elements";
import { RootState } from "types/redux/state";

/**
 * MUI Storage component
 */
const MuiStorage: React.FC<MuiStorageProps> = (props) => {
  const area = props.children;
  const usage = area.areaUsage;
  const Element = usage ? muiStorageElements[usage] : null;
  const usageLabel = usage ? constants.area.tooltips[usage] : '';
  const { bbox, scale } = useSelector(getDrawingBox);
  const { left = 0, top = 0 } = area?.location || {};
  const { length = 0, width = 0 } = area?.dimensions || {};

  // fixme I can get storageready here because I'm pasing the whole area
  const visible = area.storageReady;
  const active = useSelector((state: RootState) => getConditionResult(state, {...props.active, area: area.id}));

  const objProps = {
    x: bbox.x0 + left * scale,
    y: bbox.y0 + top * scale,
    width: length * scale,
    height: width * scale
  };

  return (
    visible && (
      <Fragment>
        {Element && <Element active={active} area={area} />}
        {!Element && (
          <foreignObject {...objProps}>
            <UsageLabel 
              active={active} 
              ver={length * scale <= 75}
            >
              {usageLabel}
            </UsageLabel>
          </foreignObject>
        )}
      </Fragment>
    )
  );
};

// Styled components using MUI styled API
const UsageLabel = styled(Typography, {
  shouldForwardProp: (prop) => !['active', 'ver'].includes(prop as string),
})<UsageLabelProps>`
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  white-space: ${({ ver }) => ver ? 'nowrap' : 'normal'};
  height: inherit;
  color: ${({ active }) => active ? 'var(--app-red)' : 'var(--app-dark-gray)'};
  transform: ${({ ver }) => `rotate(${ver ? '-90' : '0'}deg)`};
`;

export default MuiStorage;
